import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Request from '../../../../../helpers/request'
import * as url from '../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../components/Common/PaginationBar'
import { loadAddressForSwitch } from '../../../../../helpers/mesh_address'

interface Props {
    devices: any[]
    switches: any[]
    relays: any[]
    pcbs?: any[]
    loading: boolean
    userId?: string
    placeId?: string
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const ActionBtn = ({ label, color, busy, onClick, title }: {
    label: string; color: string; busy: boolean; onClick: () => void; title?: string;
}) => (
    <button
        onClick={onClick}
        disabled={busy}
        title={title || label}
        style={{
            padding: '4px 10px', fontSize: 12, fontWeight: 500,
            background: '#fff', color, border: `1px solid ${color}`,
            borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
            marginRight: 4, opacity: busy ? 0.5 : 1,
        }}
    >
        {label}
    </button>
)

const renderOnline = (isOnline: boolean | null) => {
    if (isOnline === true) return <span style={{ color: '#16A34A' }}>● Online</span>
    if (isOnline === false) return <span style={{ color: '#9CA3AF' }}>○ Offline</span>
    return <span style={{ color: '#858D9D' }}>--</span>
}

const AppliancesView = ({ devices, switches, relays, pcbs = [], loading, userId, placeId }: Props) => {
    const navigate = useNavigate()
    const { roomId } = useParams()
    const [busyDeviceId, setBusyDeviceId] = useState<string | null>(null)
    const [controlsTarget, setControlsTarget] = useState<any>(null)
    const [page, setPage] = usePersistedPage(`room-appliances-${placeId}-${roomId}`)

    if (loading) return <p style={{ color: '#858D9D' }}>Loading…</p>

    const relayMap: Record<string, string> = Object.fromEntries(
        (relays || []).map((r: any) => [r.id, r.name]),
    )
    const pcbById: Record<string, any> = Object.fromEntries((pcbs || []).map((p: any) => [p.id, p]))

    // device.id → controlling switch (first match)
    const switchByDevice: Record<string, any> = {}
    for (const s of (switches || [])) {
        if (s.deviceId && !switchByDevice[s.deviceId]) switchByDevice[s.deviceId] = s
    }

    // Panel name is derived via the controlling switch's relayId.
    const panelForDevice = (device: any) => {
        const sw = switchByDevice[device.id]
        const relayId = sw?.relayId || device.relayId
        return relayId ? (relayMap[relayId] || '') : ''
    }

    // Default (no-group) association: list all switches linked to this device.
    const controlsForDevice = (device: any) =>
        (switches || []).filter((s: any) => s.deviceId === device.id)

    const sorted = [...(devices || [])].sort((a, b) => {
        const pa = panelForDevice(a)
        const pb = panelForDevice(b)
        if (pa !== pb) return pa.localeCompare(pb)
        return (a.name || '').localeCompare(b.name || '')
    })

    const { paged, totalPages, safePage } = paginate(sorted, page)

    const pingAppliance = (device: any) => {
        // Ping the host PCB (same path the Modules tab uses) via PING_PCB.
        const sw = switchByDevice[device.id]
        const pcbId = sw?.pcbId
        if (!pcbId) {
            toast.error(`No host module found for ${device.name}`)
            return
        }
        setBusyDeviceId(device.id)
        Request.post(url.PING_PCB, { moduleId: pcbId })
            .then(() => toast.success(`Ping sent to ${device.name}`))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Ping failed'))
            .finally(() => setBusyDeviceId(null))
    }

    return (
        <div>
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <TH>Name</TH>
                            <TH>Panel</TH>
                            <TH>Online</TH>
                            <TH>Unicast</TH>
                            <TH>Actions</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {paged.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No appliances in this room
                                </td>
                            </tr>
                        )}
                        {paged.map((device, idx) => {
                            const sw = switchByDevice[device.id]
                            const online = sw ? !!sw.switchIsOnline : null
                            return (
                                <tr key={device.id || idx}>
                                    <TD>{device.name || '--'}</TD>
                                    <TD>{panelForDevice(device) || '--'}</TD>
                                    <TD>{renderOnline(online)}</TD>
                                    <TD>{(() => {
                                        const addr = loadAddressForSwitch(sw, sw ? pcbById[sw.pcbId] : null)
                                        return addr != null ? addr : '--'
                                    })()}</TD>
                                    <TD>
                                        <ActionBtn
                                            label="Ping"
                                            color="#1a73e8"
                                            busy={busyDeviceId === device.id}
                                            onClick={() => pingAppliance(device)}
                                            title="Ping this appliance's controlling switch"
                                        />
                                        <ActionBtn
                                            label="Manual Controls"
                                            color="#107569"
                                            busy={false}
                                            onClick={() => setControlsTarget(device)}
                                            title="Show manual controls linked to this appliance"
                                        />
                                        <ActionBtn
                                            label="Logs"
                                            color="#475467"
                                            busy={false}
                                            onClick={() => {
                                                if (userId && placeId) navigate(`/users/profile/${userId}/places/${placeId}/logs`)
                                            }}
                                            title="Open the Logs tab for this place"
                                        />
                                    </TD>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <PaginationBar
                    page={safePage}
                    totalPages={totalPages}
                    total={sorted.length}
                    onPage={setPage}
                />
            </div>

            {controlsTarget && (() => {
                const linked = controlsForDevice(controlsTarget)
                return (
                    <div
                        data-testid="controls-dialog"
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                        }}
                        onClick={() => setControlsTarget(null)}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: '#fff', borderRadius: 12, padding: 24,
                                maxWidth: 640, width: '90%', maxHeight: '80vh', overflow: 'auto',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            }}
                        >
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                                Manual Controls linked to <code style={{ fontSize: 13 }}>{controlsTarget.name}</code>
                            </h4>
                            {linked.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                    <thead>
                                        <tr>
                                            <TH>Name</TH>
                                            <TH>Online</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linked.map((s: any) => (
                                            <tr key={s.id}>
                                                <TD>{s.name || '--'}</TD>
                                                <TD>{renderOnline(s.switchIsOnline ?? null)}</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No manual controls linked to this appliance
                                </div>
                            )}
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setControlsTarget(null)}
                                    style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}
        </div>
    )
}

export default AppliancesView
